import polygonClipping from "polygon-clipping";
import type { LngLat } from "../types";

const METERS_PER_DEGREE_LAT = 111_320;
/** Sur des anneaux de quelques dizaines de centimètres, un angle droit arrondi en deux segments ne se distingue pas d'un arc. */
const ARC_STEP_RAD = Math.PI / 4;
/** Un sommet qui s'écarte moins que cela de la droite de ses voisins n'apporte rien à l'ombre. */
const SIMPLIFY_TOLERANCE_M = 0.02;
/** Environ un centimètre : au-delà, les décimales ne font qu'alourdir ce que MapLibre doit lire. */
const COORDINATE_PRECISION = 1e7;
const MAX_MITER = 3;
/** Deux volumes plus proches que le double de cette marge forment un seul bloc : pas d'ombre entre eux. */
const JOIN_MARGIN_M = 0.06;

/** Anneaux successifs de l'ombre, depuis son bord vers l'extérieur : leur superposition fait le dégradé. */
export const CONTACT_SHADOW_DISTANCES_M = Array.from({ length: 7 }, (_, index) => 0.17 * (index + 1));

type Point = [number, number];

interface Projection {
  toLocal: (position: LngLat) => Point;
  toLngLat: (point: Point) => LngLat;
}

function projectionAround([originLng, originLat]: LngLat): Projection {
  const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos((originLat * Math.PI) / 180);
  return {
    toLocal: ([lng, lat]) => [(lng - originLng) * metersPerDegreeLng, (lat - originLat) * METERS_PER_DEGREE_LAT],
    toLngLat: ([x, y]) => [
      Math.round((originLng + x / metersPerDegreeLng) * COORDINATE_PRECISION) / COORDINATE_PRECISION,
      Math.round((originLat + y / METERS_PER_DEGREE_LAT) * COORDINATE_PRECISION) / COORDINATE_PRECISION,
    ],
  };
}

/** Le contour ouvert, dans le sens trigonométrique. */
function counterClockwise(points: Point[]): Point[] | null {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i]!;
    const [x2, y2] = points[(i + 1) % points.length]!;
    area += x1 * y2 - x2 * y1;
  }
  if (area === 0) return null;
  return area > 0 ? points : [...points].reverse();
}

function openRing(ring: LngLat[]): LngLat[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return ring.length > 1 && first && last && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

/** Retire les sommets presque alignés avec leurs voisins, nombreux sur les contours issus d'une fusion. */
function simplify(points: Point[]): Point[] {
  const kept = [...points];
  let removed = true;
  while (removed && kept.length > 3) {
    removed = false;
    for (let i = 0; i < kept.length && kept.length > 3; i++) {
      const a = kept[(i - 1 + kept.length) % kept.length]!;
      const b = kept[i]!;
      const c = kept[(i + 1) % kept.length]!;
      const length = Math.hypot(c[0] - a[0], c[1] - a[1]);
      const deviation = length === 0 ? 0 : Math.abs((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / length;
      if (deviation < SIMPLIFY_TOLERANCE_M) {
        kept.splice(i, 1);
        removed = true;
        i--;
      }
    }
  }
  return kept;
}

function outwardNormal(from: Point, to: Point): Point {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy) || 1;
  return [dy / length, -dx / length];
}

/** Le contour `points` (ouvert, sens trigonométrique, en mètres) repoussé de `distance` vers l'extérieur, aux angles saillants arrondis. */
function offsetLocal(points: Point[], distance: number): Point[] {
  const count = points.length;
  const result: Point[] = [];
  for (let i = 0; i < count; i++) {
    const previous = points[(i - 1 + count) % count]!;
    const current = points[i]!;
    const next = points[(i + 1) % count]!;
    const n1 = outwardNormal(previous, current);
    const n2 = outwardNormal(current, next);
    const turn = (current[0] - previous[0]) * (next[1] - current[1]) - (current[1] - previous[1]) * (next[0] - current[0]);
    if (turn > 0) {
      const start = Math.atan2(n1[1], n1[0]);
      let sweep = Math.atan2(n2[1], n2[0]) - start;
      while (sweep < 0) sweep += 2 * Math.PI;
      const steps = Math.max(1, Math.ceil(sweep / ARC_STEP_RAD));
      for (let step = 0; step <= steps; step++) {
        const angle = start + (sweep * step) / steps;
        result.push([current[0] + Math.cos(angle) * distance, current[1] + Math.sin(angle) * distance]);
      }
    } else {
      const bisector: Point = [n1[0] + n2[0], n1[1] + n2[1]];
      const scale = Math.min(MAX_MITER, 2 / Math.max(1e-6, bisector[0] * bisector[0] + bisector[1] * bisector[1])) * distance;
      result.push([current[0] + bisector[0] * scale, current[1] + bisector[1] * scale]);
    }
  }
  return result;
}

/**
 * Le contour `ring` repoussé de `distance` mètres vers l'extérieur, aux angles saillants arrondis :
 * une ombre de contact se dessine par anneaux successifs, sans les pointes qu'aurait un trait épais.
 */
export function offsetRing(ring: LngLat[], distance: number): LngLat[] {
  const open = openRing(ring);
  if (open.length < 3) return [];
  const projection = projectionAround(open[0]!);
  const points = counterClockwise(open.map(projection.toLocal));
  if (!points) return [];
  const offset = offsetLocal(points, distance).map(projection.toLngLat);
  return [...offset, offset[0]!];
}

/** Un volume posé au sol et assez opaque pour qu'on n'en voie pas le pied à travers. */
function castsContactShadow(properties: GeoJSON.GeoJsonProperties): boolean {
  const height = Number(properties?.height);
  const base = Number(properties?.base ?? 0);
  const opacity = Number(properties?.volumeOpacity ?? 10);
  return height >= 0.3 && base <= 0.05 && opacity >= 8;
}

/**
 * Les volumes regroupés par contact : seuls ceux d'un même groupe ont à être fusionnés. Les boîtes
 * englobantes trient les candidats, `touch` tranche — sans lui, un grand bâtiment en L relierait tous
 * les stands posés dans sa boîte.
 */
function touchingGroups<T extends { minX: number; maxX: number; minY: number; maxY: number }>(boxes: T[], touch: (a: T, b: T) => boolean = () => true): T[][] {
  const parent = boxes.map((_, index) => index);
  const find = (index: number): number => {
    while (parent[index] !== index) index = parent[index] = parent[parent[index]!]!;
    return index;
  };
  const cellSize = 10;
  const cells = new Map<string, number[]>();
  boxes.forEach((box, index) => {
    for (let cx = Math.floor(box.minX / cellSize); cx <= Math.floor(box.maxX / cellSize); cx++) {
      for (let cy = Math.floor(box.minY / cellSize); cy <= Math.floor(box.maxY / cellSize); cy++) {
        const key = `${cx}:${cy}`;
        const members = cells.get(key) ?? [];
        for (const other of members) {
          const b = boxes[other]!;
          if (find(index) === find(other)) continue;
          if (box.minX <= b.maxX && box.maxX >= b.minX && box.minY <= b.maxY && box.maxY >= b.minY && touch(box, b)) parent[find(index)] = find(other);
        }
        members.push(index);
        cells.set(key, members);
      }
    }
  });
  const groups = new Map<number, T[]>();
  boxes.forEach((box, index) => {
    const root = find(index);
    const group = groups.get(root);
    if (group) group.push(box);
    else groups.set(root, [box]);
  });
  return [...groups.values()];
}

/** Au-delà, l'ombre d'un grand bâtiment noierait les allées voisines : le plan doit rester lisible. */
const MAX_SHADOW_LENGTH_M = 6;

export interface Sun {
  /** D'où vient la lumière, en degrés depuis le nord, dans le sens horaire. */
  azimuthDeg: number;
  /** Inclinaison par rapport à la verticale : à 0, l'ombre reste au pied du volume. */
  angleDeg: number;
}

function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point, a: Point, b: Point) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (list: Point[]) => {
    const chain: Point[] = [];
    for (const point of list) {
      while (chain.length >= 2 && cross(chain[chain.length - 2]!, chain[chain.length - 1]!, point) <= 0) chain.pop();
      chain.push(point);
    }
    chain.pop();
    return chain;
  };
  return [...half(sorted), ...half([...sorted].reverse())];
}

function isConvex(points: Point[]): boolean {
  for (let i = 0; i < points.length; i++) {
    const [ax, ay] = points[i]!;
    const [bx, by] = points[(i + 1) % points.length]!;
    const [cx, cy] = points[(i + 2) % points.length]!;
    if ((bx - ax) * (cy - by) - (by - ay) * (cx - bx) < 0) return false;
  }
  return true;
}

const closed = (ring: Point[]): Point[] => [...ring, ring[0]!];

/** L'emprise balayée par son propre décalage de `shift` : la zone à l'ombre d'un volume posé au sol. */
function sweep(points: Point[], shift: Point): polygonClipping.MultiPolygon {
  if (shift[0] === 0 && shift[1] === 0) return [[closed(points)]];
  const moved = points.map(([x, y]): Point => [x + shift[0], y + shift[1]]);
  if (isConvex(points)) return [[closed(convexHull([...points, ...moved]))]];
  const pieces: polygonClipping.Polygon[] = [[closed(points)], [closed(moved)]];
  for (let i = 0; i < points.length; i++) {
    const next = (i + 1) % points.length;
    pieces.push([closed([points[i]!, points[next]!, moved[next]!, moved[i]!])]);
  }
  return polygonClipping.union(pieces[0]!, ...pieces.slice(1));
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  const side = (p: Point, q: Point, r: Point) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const d1 = side(c, d, a);
  const d2 = side(c, d, b);
  const d3 = side(a, b, c);
  const d4 = side(a, b, d);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

/** Vrai si deux contours se chevauchent : l'un contient un sommet de l'autre, ou deux de leurs côtés se croisent. */
function ringsTouch(a: Point[], b: Point[]): boolean {
  if (containsPoint(a, b[0]!) || containsPoint(b, a[0]!)) return true;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsCross(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return true;
    }
  }
  return false;
}

function centroid(points: Point[]): Point {
  return [points.reduce((sum, [x]) => sum + x, 0) / points.length, points.reduce((sum, [, y]) => sum + y, 0) / points.length];
}

function containsPoint(ring: Point[], [px, py]: Point): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * L'ombre de chaque bloc du dernier calcul, par empreinte de ses volumes (étirés selon leur hauteur)
 * et du soleil : une modification ne recalcule que le bloc qu'elle touche.
 */
let blockShadows = new Map<string, GeoJSON.Feature[]>();

/** Empreinte des volumes, de leurs hauteurs et du soleil : tant qu'elle ne change pas, les ombres non plus. */
export function shadowKey(features: GeoJSON.Feature[], sun: Sun): string {
  return JSON.stringify([
    sun,
    features.filter((feature) => castsContactShadow(feature.properties)).map((feature) => [feature.properties?.height, feature.geometry]),
  ]);
}

/**
 * L'ombre des volumes posés au sol sous le soleil `sun` : leur emprise étirée à l'opposé du soleil
 * sur hauteur × tan(inclinaison), fusionnée entre volumes qui se touchent ou s'ombragent, puis
 * bordée d'anneaux en dégradé. Les anneaux sont évidés sous les volumes : rien ne s'affiche dans
 * l'interstice de deux volumes collés.
 */
export function shadowsToGeoJSON(features: GeoJSON.Feature[], sun: Sun): GeoJSON.FeatureCollection {
  const volumes: { ring: LngLat[]; height: number }[] = [];
  for (const feature of features) {
    if (!castsContactShadow(feature.properties)) continue;
    const height = Number(feature.properties?.height);
    const geometry = feature.geometry;
    const outerRings = geometry.type === "Polygon"
      ? [geometry.coordinates[0]]
      : geometry.type === "MultiPolygon"
      ? geometry.coordinates.map((polygon) => polygon[0])
      : [];
    for (const ring of outerRings) if (ring && ring.length >= 4) volumes.push({ ring: openRing(ring as LngLat[]), height });
  }
  if (!volumes.length) return { type: "FeatureCollection", features: [] };

  const toward = ((sun.azimuthDeg + 180) * Math.PI) / 180;
  const direction: Point = [Math.sin(toward), Math.cos(toward)];
  const slope = Math.tan((Math.min(85, Math.max(0, sun.angleDeg)) * Math.PI) / 180);

  const projection = projectionAround(volumes[0]!.ring[0]!);
  const casts: { shadow: polygonClipping.MultiPolygon; footprint: Point[]; length: number; minX: number; maxX: number; minY: number; maxY: number }[] = [];
  for (const { ring, height } of volumes) {
    const points = counterClockwise(ring.map(projection.toLocal));
    if (!points) continue;
    const footprint = offsetLocal(points, JOIN_MARGIN_M);
    const length = Math.min(MAX_SHADOW_LENGTH_M, height * slope);
    const shadow = sweep(footprint, [direction[0] * length, direction[1] * length]);
    const all = shadow.flat(2) as Point[];
    casts.push({
      shadow,
      footprint,
      length,
      minX: Math.min(...all.map(([x]) => x)),
      maxX: Math.max(...all.map(([x]) => x)),
      minY: Math.min(...all.map(([, y]) => y)),
      maxY: Math.max(...all.map(([, y]) => y)),
    });
  }

  const shadows: GeoJSON.Feature[] = [];
  const reused = new Map<string, GeoJSON.Feature[]>();
  const outers = new Map(casts.map((cast) => [cast, cast.shadow.map(([outer]) => outer as Point[])]));
  const touch = (a: (typeof casts)[number], b: (typeof casts)[number]) =>
    outers.get(a)!.some((ringA) => outers.get(b)!.some((ringB) => ringsTouch(ringA, ringB)));
  for (const group of touchingGroups(casts, touch)) {
    const key = JSON.stringify([sun, group.map(({ footprint, length }) => [length, footprint])]);
    const cached = blockShadows.get(key);
    if (cached) {
      reused.set(key, cached);
      shadows.push(...cached);
      continue;
    }
    const block: GeoJSON.Feature[] = [];
    const shadow = group.length === 1 ? group[0]!.shadow : polygonClipping.union(group[0]!.shadow, ...group.slice(1).map(({ shadow }) => shadow));
    const pieces = group.map(({ footprint }): polygonClipping.Polygon => [closed(footprint)]);
    const footprints = group.length === 1
      ? [group[0]!.footprint]
      : polygonClipping.union(pieces[0]!, ...pieces.slice(1)).map(([outer]) => (outer as Point[]).slice(0, -1));
    for (const [outer] of shadow) {
      const points = outer && counterClockwise(simplify((outer as Point[]).slice(0, -1)));
      if (!points) continue;
      const holes = footprints
        .filter((footprint) => containsPoint(points, centroid(footprint)))
        .map((footprint) => closed([...(counterClockwise(simplify(footprint)) ?? footprint)].reverse()).map(projection.toLngLat));
      for (const distance of CONTACT_SHADOW_DISTANCES_M) {
        const ring = offsetLocal(points, distance).map(projection.toLngLat);
        block.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [[...ring, ring[0]!], ...holes] } });
      }
    }
    reused.set(key, block);
    shadows.push(...block);
  }
  blockShadows = reused;
  return { type: "FeatureCollection", features: shadows };
}
